import { AnyAction, Dispatch } from 'redux'

import { getLogger } from 'shared/logger'
import { getLocaleSelector, getMpvid, getProductKey, getQuantitySelector, selectedOptionsSelector } from '~/app/store/config'

import {
  SIMILAR_PRODUCTS_ERROR,
  SIMILAR_PRODUCTS_DONE,
  SIMILAR_PRODUCTS_COUNT,
  SIMILAR_PRODUCT_LIST_BATCH_SIZE,
  SIMILAR_PRODUCTS_START,
  SIMILAR_PRODUCTS_RESET,
} from '~/app/store/similarProducts/constants'
import { getDesignCreationTypes, getNoCache, getSearchBackend, getUseConstraints } from '~/app/store/debug/reducer'

import { byId } from '~/app/utils/byId'
import { buildProductTileEntity } from '~/app/store/buildContentState'
import {
  SimilarProductsErrorAction,
  SimilarProductsFetchAction,
  SimilarProductsFetchParams,
  SimilarProductsLoadingAction,
} from '~/app/store/similarProducts/interface'
import { ProductVariationsService } from 'shared/services/ProductVariationsService'
import { arrangeProductsInOrderWithProductList, getProductsWithGallery } from '~/app/store/similarProducts/helpers'
import { ThunkDispatch } from '~/app/store/interface'

const similarProductsFetch = ({ tileEntities }: SimilarProductsFetchParams): SimilarProductsFetchAction => ({
  type: SIMILAR_PRODUCTS_DONE,
  payload: {
    tileEntities,
  },
})

const similarProductsError = (error: Error): SimilarProductsErrorAction => ({
  type: SIMILAR_PRODUCTS_ERROR,
  payload: error,
})

const similarProductsLoading = (loading: boolean): SimilarProductsLoadingAction => ({
  type: SIMILAR_PRODUCTS_START,
  payload: loading,
})

export const similarProductsReset = (): AnyAction => ({
  type: SIMILAR_PRODUCTS_RESET,
})

const fetchProductVariations = async (
  designId: string,
  productOptions: Gallery.ContentQuery.ProductOptions,
  batch: string[],
  state: State.GlobalState,
  limit: number,
  dispatch: ThunkDispatch,
): Promise<ProductVariations.ProductVariationResponse[]> => {
  const query = {
    templateToken: designId,
    locale: getLocaleSelector(state),
    selectedOptions: productOptions || selectedOptionsSelector(state),
    offset: 0,
    limit,
    noCache: getNoCache(state),
    useConstraints: getUseConstraints(state),
    mpvId: getMpvid(state),
    searchBackend: getSearchBackend(state),
    designCreationTypes: getDesignCreationTypes(state),
    productKeys: batch,
  }

  try {
    const response = await ProductVariationsService.getProductVariations(query)

    return response.results.content
  } catch (error) {
    if (process.env.IS_CLIENT) {
      getLogger().error(error as Error)
      dispatch(similarProductsError(error as Error))
    }
    throw error
  }
}

const fetchProductsInBatches = async (
  designId: string,
  productOptions: Gallery.ContentQuery.ProductOptions,
  productList: string[],
  state: State.GlobalState,
  dispatch: Dispatch,
): Promise<ProductVariations.ProductVariationResponse[]> => {
  const currentProductKey = getProductKey(state)
  const filteredProductList = productList.filter((product) => product !== currentProductKey)
  const allProducts = []
  let offset = 0

  while (offset < filteredProductList.length && allProducts.length < SIMILAR_PRODUCTS_COUNT) {
    const batch = filteredProductList.slice(offset, offset + SIMILAR_PRODUCT_LIST_BATCH_SIZE)
    const remaining: number = SIMILAR_PRODUCTS_COUNT - allProducts.length

    try {
      const products: ProductVariations.ProductVariationResponse[] = await fetchProductVariations(designId, productOptions, batch, state, remaining, dispatch)
      allProducts.push(...products)
    } catch {
      return []
    }
    offset += SIMILAR_PRODUCT_LIST_BATCH_SIZE
  }

  return arrangeProductsInOrderWithProductList(allProducts, productList)
}

const processFetchedProducts = (rawProducts: ProductVariations.ProductVariationResponse[], state: State.GlobalState): AnyAction => {
  const quantity = getQuantitySelector(state)
  const locale = getLocaleSelector(state)
  const productOptionsByProductKeyMap: Gallery.Product.ProductOptionsGroupsByProductKey = {}

  const tileEntities = rawProducts.map(
    (product): State.ProductTileEntity =>
      buildProductTileEntity({
        tileEntity: product,
        productOptionsByProductKeyMap,
        locale,
        quantity,
        isSimilarProduct: true,
      }),
  )

  const tileEntitiesById = byId<State.ProductTileEntity>(tileEntities, 'productKey')

  return similarProductsFetch({
    tileEntities: tileEntitiesById,
  })
}

export const fetchSimilarProducts =
  (designId: string, productOptions: Gallery.ContentQuery.ProductOptions) =>
  async (dispatch: ThunkDispatch, getState: () => State.GlobalState): Promise<AnyAction> => {
    const state = getState()
    const locale = getLocaleSelector(state)

    dispatch(similarProductsLoading(true))

    const productList = await getProductsWithGallery(locale)
    const allProducts = await fetchProductsInBatches(designId, productOptions, productList || [], state, dispatch)
    return dispatch(processFetchedProducts(allProducts, state))
  }
